/**
 * The open-issue table: everything Microsoft currently has unresolved against
 * this tenant, worst-affected first.
 *
 * `latest_update` is Microsoft's newest post on the issue. Graph serves it as
 * HTML; the backend strips it to plain text before it ever reaches the browser,
 * so it is rendered as ordinary text here — there is deliberately no
 * `dangerouslySetInnerHTML` anywhere in this feature.
 */

import { Badge, Card, DataGrid, EmptyState, Typography } from "@norican/react";

import type { M365Incident } from "@/lib/api";

import {
  classificationTone,
  formatDateTime,
  formatDuration,
  humanizeIssueStatus,
  incidentSearchText,
} from "./status";

interface IncidentTableProps {
  incidents: M365Incident[];
}

export function IncidentTable({ incidents }: IncidentTableProps) {
  return (
    <Card variant="outline">
      <Card.Header>
        <div className="flex items-baseline justify-between gap-4">
          <Typography variant="heading-4" as="h2">
            Active issues
          </Typography>
          <Typography variant="body-sm" tone="muted">
            {incidents.length} {incidents.length === 1 ? "issue" : "issues"} open
          </Typography>
        </div>
      </Card.Header>
      <Card.Body padding={incidents.length === 0 ? "md" : "none"}>
        {incidents.length === 0 ? (
          <EmptyState
            size="sm"
            title="Nothing open"
            description="Microsoft reports no unresolved incidents or advisories for this tenant."
          />
        ) : (
          <DataGrid<M365Incident>
            aria-label="Open Microsoft 365 service issues"
            data={incidents}
            getRowId={(incident) => incident.id}
            getRowSearchText={incidentSearchText}
            density="comfortable"
            columns={[
              {
                id: "id",
                header: "ID",
                width: 110,
                accessor: (incident) => incident.id,
                sortable: true,
                cell: (incident) => (
                  <span className="text-fg-muted font-mono text-xs">{incident.id}</span>
                ),
              },
              {
                id: "title",
                header: "Issue",
                accessor: (incident) => incident.title,
                sortable: true,
                cell: (incident) => (
                  <div className="flex max-w-xl flex-col gap-0.5">
                    <span className="text-fg font-medium">{incident.title}</span>
                    {incident.latest_update && (
                      <span className="text-fg-muted line-clamp-2 text-xs">
                        {incident.latest_update}
                      </span>
                    )}
                  </div>
                ),
              },
              {
                id: "service",
                header: "Service",
                accessor: (incident) => incident.service,
                sortable: true,
                cell: (incident) => (
                  <div className="flex flex-col">
                    <span className="text-fg text-sm">{incident.service}</span>
                    {incident.feature && (
                      <span className="text-fg-muted text-xs">{incident.feature}</span>
                    )}
                  </div>
                ),
              },
              {
                id: "classification",
                header: "Type",
                width: 120,
                accessor: (incident) => incident.classification,
                sortable: true,
                cell: (incident) => (
                  <Badge
                    tone={classificationTone(incident.classification)}
                    variant="subtle"
                    size="sm"
                  >
                    {incident.classification}
                  </Badge>
                ),
              },
              {
                id: "status",
                header: "Status",
                accessor: (incident) => incident.status,
                sortable: true,
                cell: (incident) => (
                  <span className="text-fg-muted text-sm">
                    {humanizeIssueStatus(incident.status)}
                  </span>
                ),
              },
              {
                id: "started",
                header: "Started",
                width: 150,
                accessor: (incident) => incident.start_date_time ?? null,
                sortable: true,
                cell: (incident) => (
                  <span className="text-fg-muted text-sm tabular-nums">
                    {formatDateTime(incident.start_date_time)}
                  </span>
                ),
              },
              {
                id: "duration",
                header: "Open for",
                align: "end",
                width: 120,
                accessor: (incident) => incident.start_date_time ?? null,
                cell: (incident) => (
                  <span className="text-fg tabular-nums">
                    {formatDuration(incident.start_date_time)}
                  </span>
                ),
              },
            ]}
          />
        )}
      </Card.Body>
    </Card>
  );
}
